import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Phone, Home, Layers, Zap, Hammer, MapPin, User, Mail, Wrench, HelpCircle, Paintbrush, Droplets, Flame, Mountain, Siren, Grid3x3, DoorOpen, Building2, Thermometer, Upload, X, FileText } from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import { WORK_TYPES, COMMUNES, START_TIMELINES, SURFACES } from "@/data/forms";
import { PrivacyNote, Honeypot } from "@/components/FormExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [Home, Building2, Hammer, Layers, DoorOpen, Thermometer, Paintbrush, Grid3x3, Zap, Droplets, Flame, Mountain, Siren, HelpCircle];
const projectTypes = WORK_TYPES.map((label, i) => ({ id: label, label, icon: ICONS[i] || Wrench }));
const steps = ["Projet", "Surface", "Démarrage", "Localisation", "Coordonnées"];
const MAX_FILES = 5, MAX_SIZE = 10 * 1024 * 1024;
const inputStyle = { width: '100%', padding: '14px 14px 14px 42px', borderRadius: '8px', border: '2px solid #e8e8e8', fontSize: '15px', boxSizing: 'border-box' };

function OptionList({ options, value, onSelect, prefix }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {options.map(o => (
        <button key={o} data-testid={`${prefix}-${o}`} onClick={() => onSelect(o)}
          style={{ padding: '16px 20px', borderRadius: '8px', border: value === o ? '2px solid #8B0000' : '2px solid #e8e8e8', backgroundColor: value === o ? '#fef2f2' : '#fff', cursor: 'pointer', textAlign: 'left', fontSize: '15px', fontWeight: 600, color: value === o ? '#8B0000' : '#444', transition: 'all 0.2s' }}>
          {o}
        </button>
      ))}
    </div>
  );
}

function FilePicker({ files, setFiles, error, setError }) {
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_FILES) { setError(`Maximum ${MAX_FILES} fichiers`); break; }
      if (f.size > MAX_SIZE) { setError(`${f.name} dépasse 10 Mo`); continue; }
      next.push(f);
    }
    setFiles(next);
    e.target.value = "";
  };
  return (
    <div data-testid="file-picker">
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '8px', border: '2px dashed #d4d4d4', cursor: 'pointer', color: '#666', fontSize: '14px', fontWeight: 600 }}>
        <Upload size={18} color="#8B0000" /> Ajouter des photos ou plans (optionnel, {MAX_FILES} fichiers max, 10 Mo chacun)
        <input type="file" multiple accept="image/*,.pdf" onChange={onPick} data-testid="file-input" style={{ display: 'none' }} />
      </label>
      {files.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} data-testid={`file-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#444', backgroundColor: '#fafafa', padding: '8px 12px', borderRadius: '6px' }}>
              <FileText size={14} color="#8B0000" /> <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <span style={{ color: '#999' }}>{(f.size / 1024 / 1024).toFixed(1)} Mo</span>
              <button type="button" aria-label="Retirer" onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 0 }}><X size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DemandeDevis() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ project: "", surface: "", timeline: "", commune: "", name: "", phone: "", email: "", description: "", rgpd: false, website: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const startedAt = useRef(Date.now());

  const canNext = () => {
    if (step === 0) return !!data.project;
    if (step === 1) return !!data.surface;
    if (step === 2) return !!data.timeline;
    if (step === 3) return !!data.commune;
    if (step === 4) return data.name.trim() && data.phone.trim() && data.email.trim() && data.rgpd;
    return false;
  };
  const allStepsValid = () => data.project && data.surface && data.timeline && data.commune && data.name.trim() && data.phone.trim() && data.email.trim() && data.rgpd;

  const submit = async () => {
    if (!allStepsValid()) { setError("Veuillez remplir toutes les étapes avant d'envoyer."); return; }
    setSending(true); setError("");
    try {
      let attachments = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        const up = await axios.post(`${API}/quotes/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachments = up.data.files.map(f => f.id);
      }
      await axios.post(`${API}/quotes`, {
        full_name: data.name.trim(), phone: data.phone.trim(), email: data.email.trim(),
        project_type: data.project,
        message: `Surface: ${data.surface} | Delai: ${data.timeline} | Commune: ${data.commune} | Description: ${data.description.trim() || "Non précisé"}`,
        website: data.website, elapsed_ms: Date.now() - startedAt.current, attachments,
      });
      setSent(true);
    } catch (e) { setError(e.response?.data?.detail || "Erreur lors de l'envoi. Réessayez ou appelez-nous."); }
    finally { setSending(false); }
  };

  const seo = <SEOHead title="Demande de devis gratuit – réponse sous 48 h | VGI Construct Bruxelles" description="Décrivez votre projet en 5 étapes et recevez un devis gratuit sous 48 h. Rénovation, extension, toiture, électricité à Bruxelles et dans le Brabant." path="/demande-devis" />;

  if (sent) {
    const firstName = data.name.split(" ")[0];
    return (
      <div data-testid="devis-page" style={{ backgroundColor: '#fff' }}>
        {seo}
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div data-testid="devis-success" style={{ textAlign: 'center', maxWidth: '500px' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Check size={40} color="#fff" />
            </div>
            <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '32px', fontWeight: 900, color: '#1a1a1a', marginBottom: '16px' }}>Merci {firstName} !</h1>
            <p style={{ color: '#666', fontSize: '16px', lineHeight: 1.7, marginBottom: '24px' }}>
              Votre demande a bien été envoyée. Un e-mail de confirmation vient de vous être adressé.<br />
              Nous étudions votre projet et vous recontactons <strong>sous 48 h</strong>.<br />Pour toute urgence :
            </p>
            <a href="tel:+32466407988" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#8B0000', color: '#fff', fontWeight: 700, fontSize: '16px', padding: '14px 28px', borderRadius: '100px', textDecoration: 'none' }}>
              <Phone size={20} /> +32 466 407 988
            </a>
            <div style={{ marginTop: '24px' }}>
              <Link to="/" style={{ color: '#8B0000', fontWeight: 600, textDecoration: 'none' }}>Retour à l'accueil</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="devis-page" style={{ backgroundColor: '#fff' }}>
      {seo}
      <section style={{ backgroundColor: '#0a0a0a', padding: '40px 0 20px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 24px' }}>
          <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', marginBottom: '8px' }}>Devis gratuit – réponse sous 48 h</p>
          <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, color: '#fff', marginBottom: '24px' }}>
            Décrivez votre projet en <span style={{ color: '#8B0000' }}>5 étapes</span>
          </h1>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
            {steps.map((s, i) => <div key={s} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: i <= step ? '#8B0000' : '#333', transition: 'background 0.3s' }} />)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888', fontSize: '13px' }}>Étape {step + 1}/5</span>
            <span style={{ color: '#666', fontSize: '13px' }}>{steps[step]}</span>
          </div>
        </div>
      </section>

      <section style={{ padding: '40px 0 60px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <Honeypot value={data.website} onChange={e => setData(d => ({ ...d, website: e.target.value }))} />

          {step === 0 && (
            <div data-testid="step-project">
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 800, color: '#1a1a1a', marginBottom: '24px' }}>Quel est votre projet ?</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                {projectTypes.map(p => (
                  <button key={p.id} data-testid={`project-${p.id}`} onClick={() => setData(d => ({ ...d, project: p.id }))}
                    style={{ padding: '18px 12px', borderRadius: '10px', border: data.project === p.id ? '2px solid #8B0000' : '2px solid #e8e8e8', backgroundColor: data.project === p.id ? '#fef2f2' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    <p.icon size={26} color={data.project === p.id ? '#8B0000' : '#888'} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: data.project === p.id ? '#8B0000' : '#444' }}>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-surface">
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 800, color: '#1a1a1a', marginBottom: '24px' }}>Quelle surface approximative ?</h2>
              <OptionList options={SURFACES} value={data.surface} onSelect={s => setData(d => ({ ...d, surface: s }))} prefix="surface" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-timeline">
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 800, color: '#1a1a1a', marginBottom: '24px' }}>Quand souhaitez-vous démarrer ?</h2>
              <OptionList options={START_TIMELINES} value={data.timeline} onSelect={t => setData(d => ({ ...d, timeline: t }))} prefix="timeline" />
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-location">
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 800, color: '#1a1a1a', marginBottom: '24px' }}>Où se situe le projet ?</h2>
              <div style={{ position: 'relative' }}>
                <MapPin size={18} color="#8B0000" style={{ position: 'absolute', left: '14px', top: '14px' }} />
                <select value={data.commune} onChange={e => setData(d => ({ ...d, commune: e.target.value }))} data-testid="commune-select"
                  style={{ ...inputStyle, color: '#444', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none' }}>
                  <option value="">Sélectionnez votre commune</option>
                  {COMMUNES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-contact">
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 800, color: '#1a1a1a', marginBottom: '24px' }}>Vos coordonnées</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ position: 'relative' }}>
                  <User size={18} color="#888" style={{ position: 'absolute', left: '14px', top: '14px' }} />
                  <input type="text" placeholder="Nom et prénom *" value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))} data-testid="input-name" style={inputStyle} />
                </div>
                <div style={{ position: 'relative' }}>
                  <Phone size={18} color="#888" style={{ position: 'absolute', left: '14px', top: '14px' }} />
                  <input type="tel" placeholder="Téléphone *" value={data.phone} onChange={e => setData(d => ({ ...d, phone: e.target.value }))} data-testid="input-phone" style={inputStyle} />
                </div>
                <div style={{ position: 'relative' }}>
                  <Mail size={18} color="#888" style={{ position: 'absolute', left: '14px', top: '14px' }} />
                  <input type="email" placeholder="Email *" value={data.email} onChange={e => setData(d => ({ ...d, email: e.target.value }))} data-testid="input-email" style={inputStyle} />
                </div>
                <textarea placeholder="Décrivez votre projet (optionnel)" value={data.description} onChange={e => setData(d => ({ ...d, description: e.target.value }))} rows={4} data-testid="input-description"
                  style={{ ...inputStyle, padding: '14px', resize: 'none' }} />
                <FilePicker files={files} setFiles={setFiles} error={error} setError={setError} />
                <label style={{ display: 'flex', alignItems: 'start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.rgpd} onChange={e => setData(d => ({ ...d, rgpd: e.target.checked }))} data-testid="checkbox-rgpd" style={{ marginTop: '3px', accentColor: '#8B0000', width: '18px', height: '18px' }} />
                  <span style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
                    J'accepte la <Link to="/mentions-legales" style={{ color: '#8B0000' }}>politique de confidentialité</Link> et le traitement de mes données pour répondre à ma demande. *
                  </span>
                </label>
              </div>
              {error && <p data-testid="devis-error" style={{ color: '#dc2626', fontSize: '14px', marginTop: '12px', fontWeight: 600 }}>{error}</p>}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', gap: '12px' }}>
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} data-testid="btn-prev"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '14px 24px', borderRadius: '100px', border: '2px solid #e8e8e8', backgroundColor: '#fff', fontSize: '14px', fontWeight: 700, color: '#666', cursor: 'pointer' }}>
                <ArrowLeft size={16} /> Précédent
              </button>
            ) : <div />}
            {step < 4 ? (
              <button onClick={() => canNext() && setStep(s => s + 1)} disabled={!canNext()} data-testid="btn-next"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '14px 28px', borderRadius: '100px', border: 'none', backgroundColor: canNext() ? '#8B0000' : '#ccc', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: canNext() ? 'pointer' : 'default', marginLeft: 'auto' }}>
                Suivant <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={!allStepsValid() || sending} data-testid="btn-submit"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '14px 28px', borderRadius: '100px', border: 'none', backgroundColor: allStepsValid() && !sending ? '#8B0000' : '#ccc', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: allStepsValid() && !sending ? 'pointer' : 'default', marginLeft: 'auto' }}>
                {sending ? "Envoi..." : "Envoyer ma demande"} <ArrowRight size={16} />
              </button>
            )}
          </div>
          {step === 4 && <div style={{ marginTop: '16px' }}><PrivacyNote /></div>}
        </div>
      </section>
    </div>
  );
}
